import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { IMAGES } from "@/i18n/assets";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.16 },
  }),
};

export function Hero() {
  const { t } = useLanguage();
  const { mode } = useTheme();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.12, 0.4]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
        <img src={mode === "dark" ? IMAGES.heroBgDark : IMAGES.heroBg} alt="Scottish Highlands" className="h-full w-full object-cover" />
      </motion.div>
      {/* flat wash — softens the whole image */}
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-clan-bg" />
      {/* text-column scrim (left) — keeps the headline & overline legible in both themes */}
      <div className="absolute inset-0 bg-gradient-to-r from-clan-bg via-clan-bg/65 to-transparent" />
      {/* bottom scrim for the subtitle & CTAs */}
      <div className="absolute inset-0 bg-gradient-to-t from-clan-bg via-clan-bg/35 to-transparent" />
      {/* top scrim behind the fixed navigation + overline zone */}
      <div className="absolute inset-x-0 top-0 h-[46svh] bg-gradient-to-b from-clan-bg via-clan-bg/45 to-transparent" />

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-6 pb-24 pt-28 lg:px-10 lg:pt-32"
      >
        <h1 className="hero-title pearl font-serif text-[clamp(3rem,min(13vw,16svh),11rem)] font-medium leading-[0.94] tracking-tight text-clan-ink">
          {t.hero.titleLines.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === t.hero.titleLines.length - 1 ? "italic text-sheen" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-md text-base leading-relaxed text-clan-muted md:text-lg">
            {t.hero.subtitle}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="#origins"
              data-testid="hero-primary-cta"
              className="rounded-md bg-clan-primary px-7 py-3.5 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-300 hover:bg-clan-primaryHover focus:outline-none focus:ring-2 focus:ring-clan-accent"
            >
              <span className="pearl-btn">{t.hero.cta}</span>
            </a>
            <a
              href="#join"
              data-testid="hero-secondary-cta"
              className="rounded-md border border-clan-ink/25 px-7 py-3.5 font-mono text-xs uppercase tracking-widest text-clan-ink transition-colors duration-300 hover:border-clan-accent hover:text-clan-accent focus:outline-none focus:ring-2 focus:ring-clan-accent"
            >
              {t.hero.secondary}
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
